import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, ChevronLeft, ChevronRight, Plus, Minus, Check } from 'lucide-react';
import { useCart } from './Cart';
import { useComparison } from '../hooks/useComparison.jsx';
import productService from '../services/productService';
import './ProductGrid.css';

// Fonction utilitaire pour obtenir la valeur de couleur
const getColorValue = (colorName) => {
  const colors = {
    'Bleu Titane': '#5e8ca5',
    'Noir Titane': '#444',
    'Blanc Titane': '#f0f0f0',
    'Or': '#f5d2a0',
    'Argent': '#e0e0e0',
    'Graphite': '#4d4d4d',
    'Bleu': '#0071e3',
    'Rose': '#f8b6c1',
    'Minuit': '#1a1a1a',
    'Starlight': '#f5f5f7',
    'Rouge': '#ff3b30',
    'Vert': '#34c759',
    'Violet': '#af52de',
    'Jaune': '#ffcc00'
  };
  return colors[colorName] || '#cccccc';
};

// Fonction utilitaire pour les images par défaut
const getDefaultImage = (product) => {
  // Utiliser la première image de la galerie si disponible
  if (product.image) {
    return `https://inza-backend.sajholding.org${product.image}`;
  }
  
  // Fallback vers une image par défaut locale
  return '/api/placeholder/300/300';
};

const ProductGridWithComparison = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [selectedColors, setSelectedColors] = useState({});
  const [selectedStorage, setSelectedStorage] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    priceRange: '',
    search: ''
  });

  const { addToCart, updateQuantity, getQuantity } = useCart();
  const { addToComparison, isSelected } = useComparison();

  // Charger les produits depuis l'API
  const loadProducts = useCallback(async (page = 1, append = false) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page,
        page_size: 12,
        is_active: true
      };

      // Ajouter les filtres s'ils sont définis
      if (filters.category) {
        params.category = filters.category;
      }
      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.priceRange) {
        const [min, max] = filters.priceRange.split('-');
        if (min) params.min_price = min;
        if (max) params.max_price = max;
      }

      const response = await productService.getProducts(params);
      
      if (response && Array.isArray(response)) {
        const newProducts = response;
        
        // Initialiser les quantités pour les nouveaux produits
        const newQuantities = {};
        const newColors = {};
        const newStorage = {};
        
        newProducts.forEach(product => {
          const existingQuantity = getQuantity(product.id);
          newQuantities[product.id] = existingQuantity || 1;
          newColors[product.id] = product.color || '';
          newStorage[product.id] = product.storage || '';
        });

        if (append) {
          setProducts(prev => [...prev, ...newProducts]);
          setQuantities(prev => ({ ...prev, ...newQuantities }));
          setSelectedColors(prev => ({ ...prev, ...newColors }));
          setSelectedStorage(prev => ({ ...prev, ...newStorage }));
        } else {
          setProducts(newProducts);
          setQuantities(newQuantities);
          setSelectedColors(newColors);
          setSelectedStorage(newStorage);
        }

        // Déterminer s'il y a une page suivante (basé sur le nombre de produits retournés)
        setHasNextPage(newProducts.length === 12);
      } else {
        setProducts([]);
        setHasNextPage(false);
      }
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Erreur lors du chargement des produits');
      setProducts([]);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  }, [filters, getQuantity]);

  // Charger les produits au montage et quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
    loadProducts(1, false);
  }, [loadProducts]);

  // Charger plus de produits
  const loadMore = () => {
    if (!loading && hasNextPage) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadProducts(nextPage, true);
    }
  };

  // Gérer la quantité
  const handleQuantityChange = (productId, delta) => {
    const newQuantity = Math.max(1, Math.min(10, quantities[productId] + delta));
    setQuantities(prev => ({
      ...prev,
      [productId]: newQuantity
    }));
  };

  // Ajouter au panier
  const handleAddToCart = (product) => {
    const quantity = quantities[product.id] || 1;
    const selectedColor = selectedColors[product.id] || product.color;
    const selectedStorageOption = selectedStorage[product.id] || product.storage;

    addToCart({
      ...product,
      quantity,
      selectedColor,
      selectedStorage: selectedStorageOption
    });
  };

  // Obtenir les options uniques pour les filtres
  const getUniqueCategories = () => {
    const categories = [...new Set(products.map(p => p.category_name).filter(Boolean))];
    return categories.sort();
  };

  const getUniqueColors = () => {
    const colors = [...new Set(products.map(p => p.color).filter(Boolean))];
    return colors.sort();
  };

  const getUniqueStorage = () => {
    const storage = [...new Set(products.map(p => p.storage).filter(Boolean))];
    return storage.sort();
  };

  // Gérer les changements de filtres
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      category: '',
      priceRange: '',
      search: ''
    });
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  if (loading && products.length === 0) {
    return (
      <section className="product-grid-section">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des produits...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="product-grid-section">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => loadProducts(1, false)} className="retry-button">
            Réessayer
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="product-grid-section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="section-header"
        >
          <h2 className="section-title">Nos Produits</h2>
          <p className="section-subtitle">Découvrez notre sélection de produits premium</p>
        </motion.div>

        {/* Filtres */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="filters-container"
        >
          <div className="filter-group">
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="filter-select"
            >
              <option value="">Toutes les catégories</option>
              {getUniqueCategories().map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <select
              value={filters.priceRange}
              onChange={(e) => handleFilterChange('priceRange', e.target.value)}
              className="filter-select"
            >
              <option value="">Tous les prix</option>
              <option value="0-500000">Moins de 500 000 FCFA</option>
              <option value="500000-1000000">500 000 - 1 000 000 FCFA</option>
              <option value="1000000-1500000">1 000 000 - 1 500 000 FCFA</option>
              <option value="1500000-">Plus de 1 500 000 FCFA</option>
            </select>
          </div>

          <button
            onClick={resetFilters}
            className="reset-filters-button"
          >
            Réinitialiser
          </button>
        </motion.div>

        {/* Grille de produits */}
        {products.length === 0 && !loading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="no-products"
          >
            <p>Aucun produit trouvé pour ces critères.</p>
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="product-grid"
          >
            <AnimatePresence>
              {products.map((product) => (
                <motion.div
                  key={product.id}
                  variants={itemVariants}
                  layout
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="product-card"
                >
                  <div className="product-image-container">
                    <img
                      src={getDefaultImage(product)}
                      alt={product.name}
                      className="product-image"
                      loading="lazy"
                    />
                    {product.featured && (
                      <span className="featured-badge">Nouveau</span>
                    )}
                    {/* Checkbox de comparaison */}
                    <div className="comparison-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected(product.id)}
                        onChange={() => addToComparison(product)}
                        id={`compare-${product.id}`}
                      />
                      <label htmlFor={`compare-${product.id}`}>
                        Comparer
                      </label>
                    </div>
                  </div>

                  <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-category">{product.category_name}</p>
                    
                    {/* Options de couleur */}
                    {getUniqueColors().length > 1 && (
                      <div className="color-options">
                        {getUniqueColors().slice(0, 4).map(color => (
                          <button
                            key={color}
                            className={`color-button ${selectedColors[product.id] === color ? 'selected' : ''}`}
                            style={{ backgroundColor: getColorValue(color) }}
                            onClick={() => setSelectedColors(prev => ({
                              ...prev,
                              [product.id]: color
                            }))}
                            title={color}
                          />
                        ))}
                      </div>
                    )}

                    {/* Options de stockage */}
                    {getUniqueStorage().length > 1 && (
                      <div className="storage-options">
                        {getUniqueStorage().slice(0, 2).map(storage => (
                          <button
                            key={storage}
                            className={`storage-button ${selectedStorage[product.id] === storage ? 'selected' : ''}`}
                            onClick={() => setSelectedStorage(prev => ({
                              ...prev,
                              [product.id]: storage
                            }))}
                          >
                            {storage}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="product-price-row">
                      <div className="price-container">
                        <span className="current-price">
                          {parseInt(product.price).toLocaleString('fr-FR')} FCFA
                        </span>
                        {product.original_price && (
                          <span className="original-price">
                            {parseInt(product.original_price).toLocaleString('fr-FR')} FCFA
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="product-actions">
                      <div className="quantity-selector">
                        <button
                          onClick={() => handleQuantityChange(product.id, -1)}
                          className="quantity-button"
                          disabled={quantities[product.id] <= 1}
                        >
                          <Minus size={16} />
                        </button>
                        <span className="quantity-display">{quantities[product.id]}</span>
                        <button
                          onClick={() => handleQuantityChange(product.id, 1)}
                          className="quantity-button"
                          disabled={quantities[product.id] >= 10}
                        >
                          <Plus size={16} />
                        </button>
                      </div>

                      <button
                        onClick={() => handleAddToCart(product)}
                        className="add-to-cart-button"
                      >
                        <ShoppingCart size={18} />
                        Ajouter
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Bouton charger plus */}
        {hasNextPage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="load-more-container"
          >
            <button
              onClick={loadMore}
              disabled={loading}
              className="load-more-button"
            >
              {loading ? (
                <>
                  <div className="loading-spinner-small"></div>
                  Chargement...
                </>
              ) : (
                <>
                  Charger plus de produits
                  <ChevronDown size={20} />
                </>
              )}
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ProductGridWithComparison;
